/**
 * SynonymChip - Collection-aware word chip for thesaurus synonyms
 *
 * Shows collected state with checkmark, handles optimistic toggle on click.
 * Similar pattern to RhymeResultChip but simplified for thesaurus use.
 */

import React from 'react'

interface SynonymChipProps {
  /** The synonym word to display */
  word: string
  /** Is this word already collected? */
  isCollected: boolean
  /** Click handler for collection toggle */
  onClick: () => void
}

/**
 * Collection-aware chip component for thesaurus synonyms.
 *
 * Visual states:
 * - Uncollected: Outlined chip with transparent background
 * - Collected: Filled chip with checkmark indicator
 *
 * Click behavior toggles collection state optimistically.
 */
export const SynonymChip = React.memo<SynonymChipProps>(({ word, isCollected, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`
        px-2 py-1 text-sm rounded border transition-all
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
        border-blue-300 text-blue-700
        ${isCollected ? 'bg-blue-100 font-medium' : 'bg-white hover:bg-gray-50'}
      `}
      data-testid={`synonym-chip-${word}`}
      data-collected={isCollected}
      aria-label={`${isCollected ? 'Remove' : 'Add'} ${word} to collection`}
    >
      {word}
      {isCollected && (
        <span
          className="ml-1.5 text-xs opacity-75"
          data-testid="synonym-chip-collected"
          aria-label="Already collected"
        >
          ✓
        </span>
      )}
    </button>
  )
})

SynonymChip.displayName = 'SynonymChip'
